import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { isToss, openExternal } from '../../lib/platform';
import {
  BRAND,
  DAILY_HARD_LIMIT,
  FREE_LIMIT,
  MODE_LABELS,
  ROOM_TTL_LABEL,
  TOPICS,
} from '@tarot/shared';
import { ContactForm } from './ContactForm';
import { useTrack } from '../../lib/track';

/**
 * 서비스 정보 페이지 (/info/:slug) — 푸터 메뉴에서 진입.
 * 이용안내 · FAQ · 개인정보 처리방침 · 문의하기
 * ⚠️ 타로 자체에 대한 설명(용어·스프레드·덱)은 /glossary 단일 출처. 여기 중복 서술 금지.
 * ⚠️ 횟수·인원 같은 수치는 shared 상수에서 가져올 것 (문구와 실제 동작이 어긋나지 않게).
 */

const H = ({ children }: { children: ReactNode }) => (
  <h3 className="mt-5 mb-1.5 font-semibold text-gold first:mt-0">{children}</h3>
);
const P = ({ children }: { children: ReactNode }) => (
  <p className="text-sm leading-relaxed text-moon-dim">{children}</p>
);
/**
 * 내부 링크는 `to`, 외부 링크(구글 광고 설정 등)는 `href` — 외부는 새 탭 + rel 보안 속성.
 * 토스 미니앱에서는 새 탭이 열리지 않으므로 SDK로 외부 브라우저를 띄운다 (lib/platform.ts).
 */
const A = ({ to, href, children }: { to?: string; href?: string; children: ReactNode }) => {
  const cls = 'text-gold underline underline-offset-2';
  return href ? (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={cls}
      onClick={(e) => {
        if (!isToss) return; // 웹은 브라우저 기본 동작(새 탭) 그대로
        e.preventDefault();
        void openExternal(href);
      }}
    >
      {children}
    </a>
  ) : (
    <Link to={to!} className={cls}>
      {children}
    </Link>
  );
};

const PAGES: Record<string, { title: string; body: ReactNode }> = {
  about: {
    title: '이용 안내',
    body: (
      <>
        <H>어떤 서비스인가요?</H>
        <P>
          {BRAND.nameKo}는 카드를 직접 뽑고 AI 상담사의 해석을 받는 타로 서비스예요. 혼자서도,
          친구·연인과 실시간으로 함께서도 볼 수 있어요. 회원가입 없이 바로 시작할 수 있어요.
        </P>
        <P>
          매번 <b className="text-moon">78장 전체를 섞어</b> 펼치고, 직접 뽑은 카드의 정·역방향과
          자리까지 반영해 해석해요. 정해진 문구를 돌려 쓰지 않고 그때그때 새로 씁니다. 타로 용어가
          낯설다면 <A to="/glossary">용어 사전</A>을, 카드별 의미가 궁금하면{' '}
          <A to="/cards">카드 도감</A>을 봐주세요.
        </P>

        <H>주제 고르기</H>
        <P>
          오늘의 운세를 비롯해 {TOPICS.length}가지 주제가 있어요. 주제를 고르면{' '}
          <b className="text-moon">고민을 한 줄 적는 칸</b>이 나오는데, 여기에 지금 상황을 적으면 그
          질문에 맞춰 해석해 드려요. 비워두셔도 괜찮아요.
        </P>

        <H>나 혼자 타로</H>
        <P>
          펼쳐진 덱에서 마음이 가는 카드를 아래로 끌어내리면 뽑혀요. 마음에 들지 않으면 &lsquo;다시
          섞어서 뽑기&rsquo;로 처음부터 다시 할 수 있어요.
        </P>

        <H>커플 · 우정 타로</H>
        <P>
          방을 만들고 링크나 초대코드를 공유하면 최대 {MODE_LABELS.friends.maxSeats}명까지 함께 볼
          수 있어요. 서로가 뽑는 카드가 실시간으로 보이고, 모두의 카드를 종합한 해석을 함께 받아요.
          커플방은 상대가 들어오면 자동으로 시작되고, 우정방은 방장이 시작해요. 상대가 고민 중이면
          이름 옆 종 버튼으로 살짝 재촉할 수도 있어요.
        </P>
        <P>초대 링크와 코드는 만든 뒤 {ROOM_TTL_LABEL} 동안 쓸 수 있어요.</P>

        <H>하루에 볼 수 있는 횟수</H>
        <P>
          하루 {FREE_LIMIT}번은 광고 없이 무료로 볼 수 있어요. 그다음부터는 광고 한 편을 보면 한
          번씩, 하루 {DAILY_HARD_LIMIT}번까지 이어서 볼 수 있어요. 로그인 여부와 상관없이 같고, 매일
          자정(한국시간)에 다시 채워져요.
        </P>

        <H>결과 저장과 공유</H>
        <P>
          <b className="text-moon">로그인 상태에서는 해석이 끝나면 자동으로 저장</b>돼 지난 타로에서
          다시 볼 수 있어요. 결과 화면의 공유 버튼으로 링크를 만들어 보낼 수도 있고, 이미지로 저장할
          수도 있어요.
        </P>

        <H>로그인하면 뭐가 달라지나요?</H>
        <P>
          필수가 아니에요. 로그인하면 ① 지난 타로 자동 저장·조회 ② 닉네임 관리(방 입장 시 자동 입력)
          ③ 화면 테마(시스템·라이트·다크) ④ MBTI 저장(적어두면 해석 말투가 맞춰져요)이 열려요. 하루
          이용 횟수는 로그인해도 같아요. 네이버·카카오 계정으로 가능하고(토스 앱 안에서는 토스
          계정으로), 이름·이메일은 받지 않아요.
        </P>
      </>
    ),
  },

  faq: {
    title: '자주 묻는 질문',
    body: (
      <>
        <H>Q. 무료인가요?</H>
        <P>
          네. 하루 {FREE_LIMIT}번은 광고 없이 무료로 볼 수 있어요. 그 이후로는 광고 한 편을 보면 한
          번씩, 하루 {DAILY_HARD_LIMIT}번까지 이어서 볼 수 있어요. 결제해야 하는 기능은 없어요.
        </P>

        <H>Q. 해석은 누가 해주나요?</H>
        <P>
          AI 상담사 {BRAND.nameKo}가 뽑힌 카드의 의미·방향·자리를 종합해 매번 새로 써요. 미리 만들어
          둔 문장을 골라 보여주는 방식이 아니라서, 같은 카드가 나와도 매번 다른 이야기가 나와요.
        </P>

        <H>Q. 로그인 안 하면 못 쓰는 기능이 있나요?</H>
        <P>
          모든 타로(나 혼자·커플·우정)는 비회원으로 이용할 수 있고, 하루 이용 횟수도 같아요.
          로그인해야 열리는 건 지난 타로 저장·조회, 닉네임·테마·MBTI 관리예요.
        </P>

        <H>Q. 고민 한 줄은 꼭 적어야 하나요?</H>
        <P>
          아니요, 선택이에요. 다만 적어주시면 그 상황에 맞춰 훨씬 구체적으로 읽어드릴 수 있어요.
          &ldquo;요즘 연락이 뜸해진 사람이 있어요&rdquo; 정도로 짧게 적으셔도 충분해요.
        </P>

        <H>Q. MBTI는 왜 물어보나요?</H>
        <P>
          선택 입력이에요. 알려주시면 <b className="text-moon">말을 전하는 방식</b>만 맞춰요 —
          근거와 해결책을 먼저 원하는 분께는 조언 위주로, 마음을 알아주길 바라는 분께는 공감을
          먼저요. 카드 해석 자체가 MBTI 때문에 바뀌지는 않아요.
        </P>

        <H>Q. 커플·우정 방에서 결과를 저장하려면?</H>
        <P>
          결과 화면에서 로그인하면 그 결과가 그대로 지난 타로에 저장돼요. 로그인하러 다녀오는 동안
          결과가 사라지지 않으니 안심하세요.
        </P>

        <H>Q. 방에 못 들어가요.</H>
        <P>
          초대 링크와 코드는 {ROOM_TTL_LABEL} 동안만 쓸 수 있어요. 시간이 지났거나 이미 카드 뽑기가
          시작된 방에는 들어갈 수 없으니, 새 방을 만들어 다시 초대해 주세요. 같은 방에서는 닉네임이
          겹칠 수 없어요.
        </P>

        <H>Q. 같은 질문을 여러 번 봐도 되나요?</H>
        <P>
          카드를 다시 뽑으면 새로운 해석이 나와요. 다만 같은 질문을 반복해서 보기보다는, 한 번의
          해석을 천천히 곱씹어보길 권해요.
        </P>

        <H>Q. 결과가 무섭게 나왔어요.</H>
        <P>
          타로에 &lsquo;나쁜 카드&rsquo;는 없어요. 죽음 카드도 끝과 새 시작을 뜻하는 것처럼, 모든
          카드는 흐름과 조언을 담고 있어요. 역방향도 나쁜 뜻이 아니고요(
          <A to="/glossary#upright-reversed">자세히</A>). 그리고 무엇보다 — 타로는 재미로 보는
          거예요.
        </P>

        <H>Q. 개인정보가 걱정돼요.</H>
        <P>
          이름·이메일·전화번호를 받지 않아요. 자세한 내용은{' '}
          <A to="/info/privacy">개인정보 처리방침</A>을 봐주세요.
        </P>
      </>
    ),
  },

  terms: {
    title: '서비스 이용약관',
    body: (
      <>
        <P>
          이 약관은 {BRAND.nameKo}({BRAND.nameEn}, 이하 &lsquo;서비스&rsquo;)를 이용하실 때 적용되는
          내용이에요. 어려운 말은 빼고, 실제로 지켜지는 것만 적었어요.
        </P>

        <H>1. 어떤 서비스인가요</H>
        <P>
          타로 카드를 직접 섞고 뽑으면 AI가 상담사의 말투로 해석해 드리는 서비스예요. 혼자서도,
          커플·친구와 같은 방에서 함께서도 볼 수 있어요. 회원가입 없이 이용할 수 있고, 이용료는 받지
          않아요.
        </P>

        <H>2. 타로 해석의 성격 (꼭 읽어주세요)</H>
        <P>{BRAND.disclaimer}</P>
        <P>
          해석은 <b className="text-moon">매번 새로 생성</b>돼요. 같은 카드를 뽑아도 문장과 관점이
          달라지고, 같은 결과를 다시 받을 수는 없어요. 해석의 정확성이나 특정 결과를 보장하지
          않으며, 이를 근거로 한 결정의 책임은 이용자에게 있어요.
        </P>
        <P>
          마음이 많이 힘든 상태로 보이는 표현이 담기면, 해석 대신{' '}
          <b className="text-moon">상담 창구 안내</b>를 먼저 보여드려요. 이때는 이용 횟수도 차감되지
          않아요.
        </P>

        <H>3. 이용 횟수와 광고</H>
        <P>
          하루 {FREE_LIMIT}번은 광고 없이 볼 수 있어요. 그 뒤로는 해석 한 번마다 광고 한 편을 보고
          하루 {DAILY_HARD_LIMIT}번까지 이어서 볼 수 있어요. 로그인 여부와 상관없이 같아요. 횟수는
          매일 자정(한국시간)에 다시 채워져요.
        </P>
        <P>
          서비스를 무료로 유지하기 위한 구조라, 횟수와 광고 정책은 운영 상황에 따라 조정될 수
          있어요. 바뀔 때는 서비스 안에서 안내드릴게요.
        </P>

        <H>4. 계정</H>
        <P>
          로그인은 소셜 로그인만 지원해요 — 웹에서는 네이버·카카오, 토스 앱 안의 미니앱에서는
          토스예요. 저장하는 건 제공자가 주는 고유 식별키와 직접 지으신 닉네임·설정뿐이에요 — 자세한
          내용은 <A to="/info/privacy">개인정보 처리방침</A>에 있어요.
        </P>
        <P>
          만 14세 미만은 법정대리인의 동의를 받은 뒤 이용해 주세요. 계정은 본인만 사용하셔야 하고,
          로그인 정보를 다른 사람과 공유해 생긴 문제는 책임지기 어려워요.
        </P>

        <H>5. 방과 공유 링크</H>
        <P>
          방에서는 함께한 사람들에게 닉네임과 뽑은 카드가 실시간으로 보여요. 결과를 공유 링크로
          발행하면 <b className="text-moon">링크를 아는 사람은 누구나</b> 그 결과를 볼 수 있으니,
          공개하고 싶지 않은 내용을 담아 공유하지 않도록 주의해 주세요.
        </P>
        <P>
          방은 일정 시간({ROOM_TTL_LABEL}) 뒤에 사라지고, 비회원이 만든 공유 결과는 이틀 뒤
          삭제돼요. 회원의 지난 타로는 탈퇴 전까지 보관돼요.
        </P>

        <H>6. 하지 말아야 할 것</H>
        <P>
          · 자동화 도구로 대량 요청을 보내거나 이용 횟수 제한을 우회하는 행위
          <br />· 다른 이용자를 사칭하거나, 방에서 괴롭히거나 불쾌감을 주는 닉네임·문구를 쓰는 행위
          <br />· 서비스의 정상적인 운영을 방해하거나, 허가 없이 콘텐츠를 긁어가 재배포하는 행위
          <br />· 타인의 개인정보를 고민 입력란 등에 적는 행위
        </P>
        <P>
          이런 이용이 확인되면 이용을 제한하거나 계정을 정지할 수 있어요. 78장 카드 이미지는 퍼블릭
          도메인 자료를 사용하지만, 서비스의 화면 구성과 해석 문장은 저희에게 권리가 있어요.
        </P>

        <H>7. 서비스 변경·중단</H>
        <P>
          기능은 계속 바뀌고, 점검이나 불가피한 사정으로 일시 중단될 수 있어요. 미리 알 수 있는
          경우에는 서비스 안에서 안내드릴게요. 무료로 제공되는 서비스라, 중단이나 오류로 생긴 손해에
          대해서는 저희의 고의나 중대한 과실이 있는 경우가 아니라면 책임을 지지 않아요.
        </P>

        <H>8. 약관 변경</H>
        <P>
          내용이 바뀌면 시행일 전에 서비스 안에서 안내드릴게요. 안내 후에도 계속 이용하시면 바뀐
          약관에 동의하신 것으로 볼게요. 동의하지 않으시면 이용을 중단하시거나 회원탈퇴로 언제든
          모든 기록을 지우실 수 있어요.
        </P>

        <H>9. 문의와 준거법</H>
        <P>
          문의는 <A to="/info/contact">문의하기</A> 또는 {BRAND.supportEmail}로 부탁드려요. 이
          약관은 대한민국 법을 따라요.
        </P>

        <p className="mt-4 text-xs text-moon-dim/60">
          본 약관은 {BRAND.termsEffectiveDate}부터 시행돼요.
        </p>
      </>
    ),
  },

  privacy: {
    title: '개인정보 처리방침',
    body: (
      <>
        <P>
          {BRAND.nameKo}({BRAND.nameEn})는 개인정보를 최소한으로 다루는 것을 원칙으로 해요. 아래는
          실제로 저장하는 것과 저장하지 않는 것을 있는 그대로 적은 내용이에요.
        </P>

        <H>1. 저장하는 정보</H>
        <P>
          · <b className="text-moon">비회원</b>: 계정 정보를 저장하지 않아요. 하루 이용 횟수를 세기
          위한 무작위 기기 식별값(쿠키)만 두고, 여기에는 사용자를 알아볼 수 있는 정보가 없어요.
          <br />· <b className="text-moon">회원</b>: 로그인 제공자(네이버·카카오, 토스 앱 안에서는
          토스)가 발급하는 <b className="text-moon">고유 식별키</b>, 직접 지은 닉네임, 화면 테마
          설정, 선택 입력한 MBTI.
          <br />· <b className="text-moon">타로 기록</b>: 저장·공유된 결과의 카드 구성과 해석
          텍스트, 방에서 함께한 사람들의 닉네임.
        </P>
        <P>
          이름, 이메일, 전화번호, 프로필 사진, 생년월일은{' '}
          <b className="text-moon">요청하지도, 저장하지도 않아요.</b> 소셜 로그인 시에도 식별키 외의
          항목은 동의 대상에서 제외해 두었어요.
        </P>
        <P>
          한 가지만 있는 그대로 밝혀둘게요. <b className="text-moon">토스 로그인</b>은 이름을 동의
          항목에서 뺄 수 없어서, 로그인할 때 이름이 <b className="text-moon">암호화된 형태로</b>{' '}
          저희 서버에 함께 전달돼요. 저희는 그것을 푸는{' '}
          <b className="text-moon">복호화 키를 받지 않았어요.</b> 그래서 읽을 수 없고, 저장하지도
          않아요 — 쓰는 것은 식별키뿐이에요.
        </P>

        <H>2. 이용 목적</H>
        <P>
          로그인 상태 유지, 지난 타로 조회, 방에서의 표시 이름, 하루 이용 횟수 관리, 설정(테마·MBTI)
          적용 — 이것이 전부예요.
        </P>

        <H>3. 쿠키</H>
        <P>
          · 로그인 세션 쿠키: 로그인 상태를 유지해요. 최대 7일 뒤 자동 만료돼요.
          <br />· 기기 식별 쿠키: 하루 이용 횟수를 세기 위한{' '}
          <b className="text-moon">무작위 문자열</b>이에요. 개인을 식별하거나 다른 사이트에서의
          활동을 추적하지 않아요.
          <br />이 두 가지는 저희가 직접 두는 쿠키이고, 여기에 이름·이메일 같은 개인정보는 담기지
          않아요.
        </P>

        <H>4. 광고</H>
        <P>
          서비스를 무료로 유지하기 위해 <b className="text-moon">구글 애드센스(Google AdSense)</b>
          광고를 게재해요. 이때 구글을 비롯한 제3자 광고 사업자가 쿠키를 사용해, 이 사이트나 다른
          사이트를 방문한 기록을 바탕으로 광고를 보여줄 수 있어요.
        </P>
        <P>
          · 저희는 광고 사업자에게 회원 식별키·닉네임·타로 기록을{' '}
          <b className="text-moon">전달하지 않아요.</b>
          <br />· 맞춤 광고는 <A href="https://www.google.com/settings/ads">구글 광고 설정</A>에서
          언제든 끌 수 있어요. 여러 사업자를 한 번에 관리하려면{' '}
          <A href="https://www.aboutads.info/choices/">aboutads.info</A>를 이용하시면 돼요.
          <br />· 구글이 광고 목적으로 데이터를 어떻게 다루는지는{' '}
          <A href="https://policies.google.com/technologies/partner-sites">
            구글의 파트너 사이트 정책
          </A>
          에서 확인할 수 있어요.
        </P>
        <P>
          하루 무료 횟수를 다 쓴 뒤 &lsquo;광고를 보고 이어서 보기&rsquo;를 선택하면 짧은 광고가
          나와요. 보지 않으셔도 되고, 그 경우 다음 날 자정(한국시간)에 무료 횟수가 다시 채워져요.
        </P>

        <H>5. 이용 통계</H>
        <P>
          어떤 메뉴가 얼마나 쓰이는지 알기 위해 <b className="text-moon">날짜별 이벤트 횟수</b>만
          세어요(예: &ldquo;오늘 나 혼자 타로 12회&rdquo;). 누가 눌렀는지는 기록하지 않아 특정
          개인을 되짚을 수 없어요. IP 주소는 저장하지 않아요.
        </P>

        <H>6. 제3자 제공·처리위탁</H>
        <P>
          개인정보를 제3자에게 판매하거나 제공하지 않아요. 서비스 운영을 위해 아래 업체의 인프라를
          이용해요.
          <br />· <b className="text-moon">AI 해석 생성</b>: 해석을 만들기 위해 뽑힌 카드 구성,
          주제, 방 안에서 쓰는 닉네임, (입력했다면) 고민 한 줄과 MBTI가 전달돼요. 계정 식별키는
          전달되지 않아요.
          <br />· <b className="text-moon">서버·데이터베이스</b>: Cloudflare
          <br />· <b className="text-moon">문의 메일 발송</b>: 문의하기로 보낸 내용과, 답장을 원해
          직접 적으신 이메일 주소.
          <br />· <b className="text-moon">오류 진단</b>: 서비스에 오류가 났을 때 원인을 찾기 위해
          오류 내용과 발생 위치가 전달돼요. 계정 식별키·IP 주소·입력하신 내용은 보내지 않아요.
        </P>

        <H>7. 보관과 삭제 (회원탈퇴 = 즉시 파기)</H>
        <P>
          상단 메뉴의 <b className="text-moon">내 정보</b> 화면 맨 아래{' '}
          <b className="text-moon">&lsquo;회원탈퇴&rsquo;</b>를 누르면 식별키·닉네임·설정·저장된
          타로 기록이 <b className="text-moon">그 즉시 삭제(파기)</b>되고 복구할 수 없어요. 별도
          보관 기간을 두지 않아요.
        </P>
        <P>
          하루 이용 횟수 기록과 이용 통계는 개인을 식별할 수 없는 형태라 탈퇴와 무관하게
          집계값으로만 남아요.
        </P>

        <H>8. 이용자의 권리</H>
        <P>
          저장된 정보는 <b className="text-moon">내 정보</b>(닉네임·테마·MBTI)와{' '}
          <b className="text-moon">지난 타로</b>에서 언제든 확인·수정할 수 있고, 회원탈퇴로 전부
          삭제할 수 있어요. 로그아웃만 해도 이 기기의 로그인 상태는 즉시 해제돼요.
        </P>

        <H>9. 문의</H>
        <P>
          개인정보 관련 문의는 <A to="/info/contact">문의하기</A> 또는 {BRAND.supportEmail}로
          부탁드려요. 스팸 방지를 위해 문의 횟수를 제한하는데, 이때 IP 주소는 되돌릴 수 없는 형태로
          변환해 잠시 세는 용도로만 쓰고 원본은 저장하지 않아요.
        </P>

        <p className="mt-4 text-xs text-moon-dim/60">
          본 방침은 {BRAND.privacyEffectiveDate}부터 시행돼요. 내용이 바뀔 때는 서비스 안에서 먼저
          안내드릴게요.
        </p>
      </>
    ),
  },

  contact: {
    title: '문의하기',
    body: (
      <>
        <P>궁금한 점, 불편한 점, 제안 — 무엇이든 남겨주세요.</P>
        <div className="mt-3">
          <ContactForm />
        </div>
      </>
    ),
  },
};

export const INFO_MENU = [
  { slug: 'about', label: '이용 안내' },
  { slug: 'faq', label: 'FAQ' },
  { slug: 'terms', label: '이용약관' },
  { slug: 'privacy', label: '개인정보 처리방침' },
  { slug: 'contact', label: '문의하기' },
] as const;

export function InfoPage() {
  useTrack('view:info');
  const { slug } = useParams();
  const page = PAGES[slug ?? ''];

  if (!page) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3">
        <p className="text-moon-dim">페이지를 찾을 수 없어요</p>
        <Link to="/" className="text-gold underline">
          홈으로
        </Link>
      </div>
    );
  }

  return (
    <article className="flex flex-col pb-4">
      <h2 className="mb-3 text-2xl font-bold">{page.title}</h2>
      {page.body}
    </article>
  );
}
